import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Coins, Save, Download, Loader2, Settings, Eye } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';
import { route } from '../../../shared/utils';

async function apiFetch(method: string, endpoint: string, token: string, body?: object) {
  const res = await fetch(`${config.api.baseUrl}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
      [config.auth.headers.auth]: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    console.error(`[apiFetch] ${method} ${endpoint} → ${res.status}`, text);
    return null;
  }
  return res.json();
}

const SERVICE_NAMES: Record<string, string> = {
  plot_regular: 'Regular Plot',
  plot_regular_room: 'Regular Plot + Room',
  plot_irregular: 'Irregular Plot',
  plot_irregular_room: 'Irregular Plot + Room',
};

// Cache buster for the iframe — stable per page load (dev: timestamp,
// prod: bumped on each deploy via script-embed asset hash isn't possible
// since it's served as a static legacy file, so we use the build mode).
const IFRAME_CACHE_KEY = import.meta.env.DEV ? String(Date.now()) : 'r3';

function useIsMobile(breakpoint = 1024) {
  const [mobile, setMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [breakpoint]);
  return mobile;
}

const GeneratorPage: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isMobile = useIsMobile();

  const [serviceCode, setServiceCode] = useState(searchParams.get('service') || 'plot_regular');
  const contentMode = searchParams.get('content') || 'sample';
  const designId = searchParams.get('designId');

  const [balance, setBalance] = useState<number | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadCost, setDownloadCost] = useState(0);
  const [isReDownload, setIsReDownload] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [currentDesignId, setCurrentDesignId] = useState<string | null>(designId);
  const defaultName = `${SERVICE_NAMES[serviceCode] || 'Plot'} #${Date.now().toString(36).slice(-4).toUpperCase()}`;
  const [currentPlotName, setCurrentPlotName] = useState(defaultName);
  const [currentFormData, setCurrentFormData] = useState<any>(null);
  const [mobileTab, setMobileTab] = useState<'controls' | 'preview'>('controls');

  // Handle payment return query params
  const [paymentBanner, setPaymentBanner] = useState<string | null>(null);
  useEffect(() => {
    const p = searchParams.get('payment');
    if (p === 'success') {
      const credits = searchParams.get('credits') || '0';
      setPaymentBanner(`Payment successful! ₹${credits} credits added.`);
      // Clean up URL
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('credits');
      setSearchParams(next, { replace: true });
    } else if (p === 'failed') {
      setPaymentBanner('Payment failed or cancelled. No amount charged.');
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    }
  }, []);

  // Fetch balance
  useEffect(() => {
    if (!token) return;
    apiFetch('GET', '/api/credit/balance', token).then(res => {
      if (res?.data) setBalance(res.data.balance ?? 0);
    });
  }, [token]);

  // Listen for postMessages from iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg || !msg.type) return;
      switch (msg.type) {
        case 'GENERATOR_READY':
          setIframeReady(true);
          break;
        case 'FORM_CHANGED':
          if (msg.plotName && !/^Plot \d+$/i.test(msg.plotName)) setCurrentPlotName(msg.plotName);
          setCurrentFormData(msg.formData || null);
          break;
        case 'FORM_DATA':
          if (msg.plotName && !/^Plot \d+$/i.test(msg.plotName)) setCurrentPlotName(msg.plotName);
          setCurrentFormData(msg.formData || null);
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Once iframe is ready, send template or load design
  useEffect(() => {
    if (!iframeReady || !iframeRef.current?.contentWindow) return;
    if (currentDesignId) {
      // Load existing design
      if (!token) return;
      apiFetch('GET', `/api/design/${currentDesignId}`, token).then(res => {
        const d = res?.data?.design || res?.data;
        if (d) {
          setCurrentPlotName(d.name);
          if (d.serviceCode) setServiceCode(d.serviceCode);
          iframeRef.current?.contentWindow?.postMessage({
            type: 'LOAD_DESIGN',
            plotData: d.plotData,
          }, '*');
        }
      });
    } else {
      // Apply template from URL params with default name
      iframeRef.current.contentWindow.postMessage({
        type: 'APPLY_TEMPLATE',
        serviceCode,
        content: contentMode,
        plotName: defaultName,
      }, '*');
    }
  }, [iframeReady]);

  // Send tab switch to iframe when mobile tab changes
  useEffect(() => {
    if (!isMobile || !iframeReady || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ type: 'SWITCH_TAB', tab: mobileTab }, '*');
  }, [mobileTab, isMobile, iframeReady]);

  // On initial load on mobile, default to controls tab
  useEffect(() => {
    if (isMobile && iframeReady && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'SWITCH_TAB', tab: 'controls' }, '*');
    }
  }, [iframeReady, isMobile]);

  const requestFormData = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'GET_FORM_DATA' }, '*');
  }, []);

  const handleSave = async (): Promise<string | null> => {
    if (!token) return null;
    setSaving(true);
    setSaveMsg('');
    // Get latest form data
    requestFormData();
    await new Promise(r => setTimeout(r, 600));
    // Use currentFormData captured from iframe
    const formData = currentFormData;
    const name = currentPlotName;
    let savedId: string | null = null;
    try {
      const body: any = {
        serviceCode,
        name,
        plotData: formData ?? {},
      };
      if (currentDesignId) body.id = currentDesignId;
      const res = await apiFetch('POST', '/api/design/save', token, body);
      const saved = res?.data?.design || res?.data;
      if (saved?.id) {
        savedId = saved.id as string;
        setCurrentDesignId(savedId);
        // Persist design id in the URL so a refresh re-loads this saved
        // design instead of resetting to the sample template.
        const next = new URLSearchParams(searchParams);
        next.set('designId', savedId);
        next.delete('content');
        setSearchParams(next, { replace: true });
        setSaveMsg('Saved!');
        setTimeout(() => setSaveMsg(''), 2000);
      } else {
        console.error('[handleSave] save failed, response:', res);
        setSaveMsg('Save failed');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch (err) {
      console.error('[handleSave] exception:', err);
      setSaveMsg('Save error');
      setTimeout(() => setSaveMsg(''), 3000);
    }
    setSaving(false);
    return savedId;
  };

  const handleDownloadClick = async () => {
    if (!token) return;
    setDownloadError(null);

    // Auto-save first if no designId
    let designId = currentDesignId;
    if (!designId) {
      designId = await handleSave();
      await new Promise(r => setTimeout(r, 300));
    }
    if (!designId) return;

    // Fetch design (to check downloadCount) + service pricing in parallel
    const [designRes, svcRes] = await Promise.all([
      apiFetch('GET', `/api/design/${designId}`, token),
      apiFetch('GET', '/api/service/list', token),
    ]);

    const design = designRes?.data?.design || designRes?.data;
    const services: any[] = svcRes?.data || [];
    const svc = services.find((s: any) => s.code === serviceCode);

    const reDownload = (design?.downloadCount ?? 0) > 0;
    const cost = reDownload ? (svc?.tier2Price ?? 0) : (svc?.tier1Price ?? 0);

    setIsReDownload(reDownload);
    setDownloadCost(cost);
    setShowDownloadDialog(true);
  };

  const handleConfirmDownload = async () => {
    if (!token || !currentDesignId) return;
    setDownloading(true);
    setShowDownloadDialog(false);
    setDownloadError(null);

    try {
      // Deduct credits + get token
      const res = await apiFetch('POST', '/api/download/token', token, { designId: currentDesignId });
      if (!res?.data?.tokenId) {
        const msg = res?.message || 'Insufficient balance or download failed.';
        setDownloadError(msg);
        setDownloading(false);
        return;
      }

      const tokenId = res.data.tokenId;

      // Trigger PDF in iframe
      iframeRef.current?.contentWindow?.postMessage({ type: 'TRIGGER_DOWNLOAD' }, '*');

      // Confirm download record
      await apiFetch('POST', '/api/download/confirm', token, { tokenId });

      // Refresh balance
      const balRes = await apiFetch('GET', '/api/credit/balance', token);
      if (balRes?.data) setBalance(balRes.data.balance ?? 0);
    } catch (err: any) {
      setDownloadError(err.message || 'Download failed. Please try again.');
    }

    setDownloading(false);
  };

  const switchTab = (tab: 'controls' | 'preview') => {
    setMobileTab(tab);
  };

  if (!user) return null;

  const serviceName = SERVICE_NAMES[serviceCode] || serviceCode;
  const TAB_BAR_HEIGHT = 56;

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#242424' }}>
      <SEO title={`${serviceName} - RRD Planners`} description="Plot map generator" />

      {/* Toolbar */}
      <div style={{
        flexShrink: 0,
        background: '#0d0d0d', borderBottom: '1px solid #2a2a2a',
        display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12,
        padding: isMobile ? '0 8px' : '0 16px', height: isMobile ? 44 : 52,
        zIndex: 100,
      }}>
        <button
          onClick={() => navigate(route('/dashboard'))}
          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px', borderRadius: 6, flexShrink: 0 }}
        >
          <ArrowLeft size={16} />
          {!isMobile && <span style={{ fontSize: 13 }}>Back</span>}
        </button>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ color: '#FFD93D', fontWeight: 700, fontSize: isMobile ? 13 : 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{serviceName}</span>
          {!isMobile && currentPlotName && (
            <span style={{ color: '#666', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>/ {currentPlotName}</span>
          )}
        </div>

        {/* Credit balance */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#FFD93D', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          <Coins size={14} />
          <span>{balance ?? '...'}</span>
        </div>

        {/* Desktop-only: Save button */}
        {!isMobile && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: saving ? '#333' : '#2a2a2a',
              border: '1px solid #444', borderRadius: 6,
              color: saveMsg ? '#4CAF50' : '#ddd',
              fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer',
            }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saveMsg || 'Save'}
          </button>
        )}

        {/* Desktop-only: Download PDF button */}
        {!isMobile && (
          <button
            onClick={handleDownloadClick}
            disabled={downloading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#FFD93D', color: '#1a1a1a',
              border: 'none', borderRadius: 6,
              fontSize: 12, fontWeight: 700, padding: '6px 14px', cursor: 'pointer',
            }}
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download PDF
          </button>
        )}
      </div>

      {/* Iframe — fills remaining height, minus mobile tab bar */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', marginBottom: isMobile ? TAB_BAR_HEIGHT : 0 }}>
        {!iframeReady && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a' }}>
            <Loader2 size={32} className="animate-spin" style={{ color: '#FFD93D' }} />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={`/legacy/generator-embed.html?v=${IFRAME_CACHE_KEY}`}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: iframeReady ? 'block' : 'none',
          }}
          title="Plot Generator"
        />
      </div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: TAB_BAR_HEIGHT,
          background: '#0d0d0d', borderTop: '1px solid #333',
          display: 'flex', zIndex: 100,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {/* Controls tab */}
          <button
            onClick={() => switchTab('controls')}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none',
              color: mobileTab === 'controls' ? '#FFD93D' : '#666',
              fontSize: 10, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Settings size={20} />
            Controls
          </button>

          {/* Preview tab */}
          <button
            onClick={() => switchTab('preview')}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none',
              color: mobileTab === 'preview' ? '#FFD93D' : '#666',
              fontSize: 10, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Eye size={20} />
            Preview
          </button>

          {/* Save tab */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none',
              color: saveMsg ? '#4CAF50' : '#666',
              fontSize: 10, fontWeight: 600, cursor: 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            {saveMsg || 'Save'}
          </button>

          {/* Download PDF tab */}
          <button
            onClick={handleDownloadClick}
            disabled={downloading}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none',
              color: '#FFD93D',
              fontSize: 10, fontWeight: 700, cursor: 'pointer',
              opacity: downloading ? 0.5 : 1,
            }}
          >
            {downloading ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
            PDF
          </button>
        </div>
      )}

      {/* Download Confirmation Dialog */}
      {showDownloadDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 16,
            padding: isMobile ? 20 : 28, maxWidth: 380, width: '100%',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, background: 'rgba(255,217,61,0.1)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Download size={20} style={{ color: '#FFD93D' }} />
              </div>
              <div>
                <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Download PDF</div>
                <div style={{ color: '#666', fontSize: 12 }}>{isReDownload ? 'Re-download (edit rate applies)' : 'First download'}</div>
              </div>
            </div>

            {/* Cost breakdown */}
            <div style={{ background: '#111', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: '#888', fontSize: 13 }}>{isReDownload ? 'Edit rate' : 'New plan rate'}</span>
                <span style={{ color: '#FFD93D', fontSize: 15, fontWeight: 700 }}>₹{downloadCost}</span>
              </div>
              <div style={{ height: 1, background: '#222', marginBottom: 10 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888', fontSize: 13 }}>Your balance</span>
                <span style={{ color: (balance ?? 0) >= downloadCost ? '#aaa' : '#ef4444', fontSize: 13, fontWeight: 600 }}>
                  ₹{balance ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ color: '#888', fontSize: 13 }}>After deduction</span>
                <span style={{ color: '#aaa', fontSize: 13 }}>₹{Math.max(0, (balance ?? 0) - downloadCost)}</span>
              </div>
            </div>

            {/* Insufficient balance — pay directly */}
            {(balance ?? 0) < downloadCost && (
              <div style={{ background: 'rgba(255,217,61,0.06)', border: '1px solid rgba(255,217,61,0.2)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <p style={{ color: '#FFD93D', fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>
                  Insufficient balance — ₹{downloadCost - (balance ?? 0)} more needed
                </p>
                <p style={{ color: '#888', fontSize: 12, margin: '0 0 10px' }}>
                  Pay now and credits will be added instantly to your wallet.
                </p>
                <button
                  onClick={async () => {
                    const needed = downloadCost - (balance ?? 0);
                    try {
                      const returnUrl = window.location.pathname + window.location.search;
                      const res = await apiFetch('POST', '/api/payment/create-order', token!, { customAmount: needed, returnUrl });
                      const data = res?.data;
                      if (data?.payuFormData && data?.payuBaseUrl) {
                        const form = document.createElement('form');
                        form.method = 'POST';
                        form.action = data.payuBaseUrl;
                        for (const [key, value] of Object.entries(data.payuFormData)) {
                          const input = document.createElement('input');
                          input.type = 'hidden';
                          input.name = key;
                          input.value = String(value);
                          form.appendChild(input);
                        }
                        document.body.appendChild(form);
                        form.submit();
                      }
                    } catch { }
                  }}
                  style={{
                    width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                    background: '#FFD93D', color: '#1a1a1a', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Pay ₹{downloadCost - (balance ?? 0)} Now
                </button>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowDownloadDialog(false)}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid #333',
                  background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDownload}
                disabled={(balance ?? 0) < downloadCost}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 8, border: 'none',
                  background: (balance ?? 0) < downloadCost ? '#555' : '#FFD93D',
                  color: '#1a1a1a', cursor: (balance ?? 0) < downloadCost ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 700,
                }}
              >
                Confirm & Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment return banner */}
      {paymentBanner && (
        <div style={{
          position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
          background: paymentBanner.includes('success') ? 'rgba(34,197,94,0.95)' : 'rgba(239,68,68,0.95)',
          color: '#fff', borderRadius: 10, padding: '12px 20px', zIndex: 300, fontSize: 13,
          fontWeight: 600, maxWidth: 360, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {paymentBanner}
          <button onClick={() => setPaymentBanner(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* Download error toast */}
      {downloadError && (
        <div style={{
          position: 'fixed', bottom: isMobile ? TAB_BAR_HEIGHT + 12 : 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1e1e1e', border: '1px solid #ef4444', borderRadius: 10,
          padding: '12px 20px', zIndex: 300, color: '#ef4444', fontSize: 13, maxWidth: 360,
        }}>
          {downloadError}{' '}
          <button onClick={() => setDownloadError(null)} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', marginLeft: 8 }}>✕</button>
        </div>
      )}

      {/* Save success toast */}
      {saveMsg && isMobile && (
        <div style={{
          position: 'fixed', bottom: TAB_BAR_HEIGHT + 12, left: '50%', transform: 'translateX(-50%)',
          background: '#1e1e1e', border: '1px solid #4CAF50', borderRadius: 10,
          padding: '10px 20px', zIndex: 300, color: '#4CAF50', fontSize: 13,
        }}>
          {saveMsg}
        </div>
      )}
    </div>
  );
};

export default GeneratorPage;
